import type { ItemCounts, ItemView } from '@nelnela/contracts';

/**
 * Words' decisions (M10-iii), kept out of the screen so they can be tested — the split
 * `lib/cards.ts` and `lib/placement.ts` already make, for M6c's reason: the web suite is node with
 * no DOM.
 *
 * THE SHAPE α SETTLED, and it is not the obvious one. The catalog pivot is a SCOPE PAIR above the
 * list — "My words · 214 / Catalog · 5,695" — and NOT a fifth segment, because the segments mean
 * content type (vocabulary, patterns, grammar, saved) and scope is a different question about the
 * same content. Folding them into one control would have made "Catalog" exclusive with "Patterns",
 * which is not how anyone thinks about a dictionary.
 */

export type Scope = 'met' | 'verified';
export type StateFilter = 'all' | 'unmet' | 'shaky' | 'solid';

/**
 * Which state chips a scope offers.
 *
 * "not met" exists only in the catalog: in My words every row has been met by definition, so the
 * chip would be a filter that always returns nothing. In the catalog it is the most useful filter
 * on the screen — it is the answer to "what is left in this band".
 */
export function stateChips(scope: Scope): StateFilter[] {
  return scope === 'verified' ? ['all', 'unmet', 'shaky', 'solid'] : ['all', 'shaky', 'solid'];
}

/** A state filter that a scope does not offer must not survive a scope switch. */
export function keepState(scope: Scope, state: StateFilter): StateFilter {
  return stateChips(scope).includes(state) ? state : 'all';
}

/** "My words · 214" / "Catalog · 5,695" — honest counts, thousands separated. */
export function scopeLabel(scope: Scope, counts: ItemCounts | undefined): string {
  const name = scope === 'met' ? 'My words' : 'Catalog';
  const n = counts ? (scope === 'met' ? counts.scopes.met : counts.scopes.verified) : null;
  return n === null ? name : `${name} · ${n.toLocaleString('en-US')}`;
}

export interface Band {
  level: number;
  label: string;
  /** "291 words · 173 met" — a fact of coverage, never a percentage */
  detail: string;
  words: number;
  met: number;
}

export const bandLabel = (level: number): string => `LEVELS ${level}–${level + 9}`;

export function bands(counts: ItemCounts | undefined): Band[] {
  return (counts?.bands ?? []).map((b) => ({
    level: b.level,
    label: bandLabel(b.level),
    // α: "bars are met-count over band size — a fact of coverage, not a score." So a fraction of a
    // known whole, and never a percentage: 0 of 276 says something true about a band past the
    // learner's edge that "0 %" says worse.
    detail: `${b.words.toLocaleString('en-US')} words · ${b.met.toLocaleString('en-US')} met`,
    words: b.words,
    met: b.met,
  }));
}

/**
 * The list, cut into bands with sticky headers.
 *
 * Only ever grouped in the catalog, and only when the rows are in level order — a search spans
 * every band at once and a My-words list is ordered by what is due, so a header over either would
 * be a heading for a group that is not there. `rowTrailing` below is what carries the level in
 * those cases instead.
 */
export interface Group {
  level: number | null;
  items: ItemView[];
}

export function groupByBand(items: readonly ItemView[], grouped: boolean): Group[] {
  if (!grouped) return items.length ? [{ level: null, items: [...items] }] : [];
  const out: Group[] = [];
  for (const v of items) {
    const level = v.item.level;
    const band =
      level === null || level === undefined ? null : Math.floor((level - 1) / 10) * 10 + 1;
    const last = out[out.length - 1];
    if (last && last.level === band) last.items.push(v);
    else out.push({ level: band, items: [v] });
  }
  return out;
}

/** Whether the list is cut into bands at all. See `groupByBand`. */
export const isGrouped = (scope: Scope, search: string): boolean =>
  scope === 'verified' && search.trim() === '';

/**
 * What a row shows on its right-hand side, which differs by scope — α: "my-words rows trade the
 * band chip for the due column".
 *
 * In My words the useful fact is when it comes back. In the catalog it is where the word sits, and
 * that is normally carried by the band header above it — so a row says a level ONLY when there is
 * no header to say it, which is exactly the search case. A level chip under a "LEVELS 21–30"
 * header would be the same number twice.
 */
export type Trailing =
  { kind: 'due'; text: string } | { kind: 'level'; text: string } | { kind: 'none' };

export function rowTrailing(v: ItemView, scope: Scope, grouped: boolean, now: number): Trailing {
  if (scope === 'met') return { kind: 'due', text: dueText(v, now) };
  if (grouped) return { kind: 'none' };
  const level = v.item.level;
  return level === null || level === undefined
    ? { kind: 'none' }
    : { kind: 'level', text: String(level) };
}

/**
 * "due today" / "3d".
 *
 * Days, never hours or a date. The engine schedules in days (FSRS short-term is off by design),
 * so an hour is a precision the number does not have, and a calendar date makes the learner do
 * the subtraction.
 *
 * CALENDAR DAYS, not elapsed milliseconds — and the difference is not pedantic. Dividing a raw
 * span by 86 400 000 makes a word due in one hour "1d" and a word due in twenty-five hours "2d",
 * so the number a learner reads depends on the time of day they happen to look. Flooring both ends
 * to local midnight first is what makes "due today" mean today.
 */
export function dueText(v: ItemView, now: number): string {
  const due = soonestDue(v);
  // No date at all: a word that has been saved or seen but never graded. "not met" is what the
  // state chips call that, and the two must not disagree on the same screen.
  if (due === null) {
    const overall = v.memory?.overall;
    return overall === undefined || overall === 'unmet' ? 'not met' : 'not scheduled';
  }
  const days = Math.round((midnight(due) - midnight(now)) / 86_400_000);
  return days <= 0 ? 'due today' : `${days}d`;
}

/** Local midnight at or before `t`. Local, because "today" is the learner's, not UTC's. */
function midnight(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** The nearest due date across axes — the one that decides when the word comes back. */
export function soonestDue(v: ItemView): number | null {
  let best: number | null = null;
  for (const axis of Object.values(v.memory?.axes ?? {})) {
    if (!axis?.dueAt) continue;
    const t = Date.parse(axis.dueAt);
    if (Number.isNaN(t)) continue;
    if (best === null || t < best) best = t;
  }
  return best;
}

/**
 * The ვ‑ chip: this word is a verb, and it has a paradigm worth opening.
 *
 * Driven by the item's part of speech rather than by whether forms happen to be loaded, so the
 * chip is stable while the panel's data is not. The forms panel behind it stays shut until the
 * UniMorph gate opens (M10-i § What is DISABLED on purpose) — the chip is how a learner finds out
 * it exists at all.
 */
export const isVerb = (v: ItemView): boolean => (v.item.pos ?? '').toLowerCase().startsWith('verb');
